import { getYtdDateRange } from '../utils/dateRanges';

/**
 * Relative Strength timeframe windows (separate from Index comparison).
 * Labels match product copy; periodRank orders multi-column display shortest → longest.
 */

export const RS_TIMEFRAMES = [
  // Intraday — available in multi-select (and useful for RS during the session).
  {
    key: '15m',
    label: '15m',
    interval: 15,
    unit: 'Minute',
    barsback: 78,
    minBars: 16,
    periodRank: 15,
    single: false,
  },
  {
    key: '1h',
    label: '1h',
    interval: 60,
    unit: 'Minute',
    barsback: 78,
    minBars: 16,
    periodRank: 60,
    single: false,
  },
  // Primary single-mode intervals (also selectable in multi).
  {
    key: 'today',
    label: 'Today',
    interval: 5,
    unit: 'Minute',
    barsback: 200,
    minBars: 20,
    periodRank: 390,
    single: true,
  },
  {
    key: 'week',
    label: 'Week',
    interval: 30,
    unit: 'Minute',
    barsback: 80,
    minBars: 16,
    periodRank: 2520,
    single: true,
  },
  {
    // Study-style weekly RS (12 weekly bars). Distinct from "Week" (30-minute session).
    key: 'weekly',
    label: 'Weekly',
    interval: 1,
    unit: 'Weekly',
    barsback: 12,
    minBars: 8,
    periodRank: 8000,
    single: true,
  },
  {
    key: 'month',
    label: 'Month',
    interval: 1,
    unit: 'Daily',
    barsback: 21,
    minBars: 10,
    periodRank: 10000,
    single: true,
  },
  {
    key: 'quarter',
    label: 'Quarter',
    interval: 1,
    unit: 'Daily',
    barsback: 63,
    minBars: 20,
    periodRank: 30000,
    single: true,
  },
  {
    key: 'ytd',
    label: 'YTD',
    interval: 1,
    unit: 'Daily',
    range: 'ytd',
    barsback: 260,
    minBars: 10,
    periodRank: 50000,
    single: true,
  },
  {
    key: '1y',
    label: '1Y',
    interval: 1,
    unit: 'Daily',
    barsback: 252,
    minBars: 40,
    periodRank: 100000,
    single: true,
  },
];

/** Legacy Market Overview keys → RS keys (saved appSettings / local state). */
const LEGACY_KEY_MAP = {
  '1D': 'today',
  '1W': 'week',
  '1M': 'month',
  '3M': 'quarter',
  '6M': 'quarter',
  YTD: 'ytd',
  '1Y': '1y',
  '1wk': 'weekly',
  '1WK': 'weekly',
};

export const RS_MULTI_TIMEFRAME_OPTIONS = [...RS_TIMEFRAMES].reverse();

export const RS_SINGLE_TIMEFRAMES = RS_MULTI_TIMEFRAME_OPTIONS.filter((tf) => tf.single);

export const RS_MAX_MULTI_TIMEFRAMES = 4;

export function normalizeRsTimeframeKey(raw) {
  const key = String(raw || '').trim();
  const mapped = LEGACY_KEY_MAP[key] || key.toLowerCase();
  if (RS_TIMEFRAMES.some((tf) => tf.key === mapped)) return mapped;
  return 'today';
}

export function getRsTimeframe(key) {
  const normalized = normalizeRsTimeframeKey(key);
  return RS_TIMEFRAMES.find((tf) => tf.key === normalized) || RS_TIMEFRAMES.find((tf) => tf.key === 'today');
}

export function orderRsTimeframeKeys(keys) {
  const unique = [];
  const seen = new Set();
  (keys || []).forEach((raw) => {
    const key = normalizeRsTimeframeKey(raw);
    if (seen.has(key)) return;
    if (!RS_TIMEFRAMES.some((tf) => tf.key === key)) return;
    seen.add(key);
    unique.push(key);
  });
  return unique
    .map((key) => getRsTimeframe(key))
    .sort((a, b) => b.periodRank - a.periodRank)
    .slice(0, RS_MAX_MULTI_TIMEFRAMES)
    .reverse()
    .map((tf) => tf.key);
}

export function resolveRsBarQuery(tf, now = new Date()) {
  if (!tf) return {};
  if (tf.range === 'ytd') {
    const { firstdate, lastdate } = getYtdDateRange(now);
    return { firstdate, lastdate };
  }
  return { barsback: tf.barsback };
}

/** Plotted RS points (after the score window) so daily/weekly charts span years, not months. */
const HISTORY_PLOT_BARS = {
  Daily: 252, // ~1 year of daily RS
  Weekly: 104, // ~2 years of weekly RS
};

/**
 * Extra bars so a rolling RS window can be plotted over time.
 * Daily: ~1 year of scores; weekly: ~2 years. Intraday stays a few window-lengths.
 * YTD still starts at Jan 1 because that window is calendar-to-date.
 */
export function resolveRsHistoryBarQuery(tf, now = new Date()) {
  if (!tf) return {};
  if (tf.range === 'ytd') {
    const { firstdate, lastdate } = getYtdDateRange(now);
    return { firstdate, lastdate };
  }
  const windowBars = Number(tf.barsback) || 21;
  const plotBars = HISTORY_PLOT_BARS[tf.unit];
  if (Number.isFinite(plotBars)) {
    return { barsback: windowBars + plotBars };
  }
  const intradayPlot = Math.min(Math.max(windowBars * 3, 120), 400);
  return { barsback: windowBars + intradayPlot };
}
